import { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle } from 'react-leaflet';
import L from 'leaflet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  MapPin, Navigation, Loader2, RefreshCw, Share2, 
  Eye, EyeOff, Smartphone, Laptop, User, Clock,
  Wifi, WifiOff, Zap
} from 'lucide-react';
import api from '@/lib/api';
import 'leaflet/dist/leaflet.css';

// Fix pour les icônes Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Icône pulsante personnalisée (style Snapchat) - Améliorée pour mobile
const createPulsingIcon = (color = '#22c55e', isMe = false) => {
  const size = isMe ? 20 : 16;
  const pulseSize = isMe ? 60 : 50;
  
  return L.divIcon({
    className: `pulsing-marker-${isMe ? 'me' : 'other'}`,
    html: `
      <div class="pulse-wrapper" style="
        position: relative;
        width: ${size}px;
        height: ${size}px;
      ">
        <div class="pulse-ring-1" style="
          position: absolute;
          width: ${pulseSize}px;
          height: ${pulseSize}px;
          border-radius: 50%;
          background: ${color};
          opacity: 0.3;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          animation: pulse-expand 2s ease-out infinite;
        "></div>
        <div class="pulse-ring-2" style="
          position: absolute;
          width: ${pulseSize}px;
          height: ${pulseSize}px;
          border-radius: 50%;
          background: ${color};
          opacity: 0.2;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          animation: pulse-expand 2s ease-out infinite 0.5s;
        "></div>
        <div class="pulse-core" style="
          position: absolute;
          width: ${size}px;
          height: ${size}px;
          border-radius: 50%;
          background: ${color};
          border: 3px solid white;
          box-shadow: 0 2px 10px rgba(0,0,0,0.4);
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 10;
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          ${isMe ? `<div style="width:8px;height:8px;background:white;border-radius:50%;animation:blink 1.5s ease-in-out infinite;"></div>` : ''}
        </div>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size/2, size/2],
    popupAnchor: [0, -size/2]
  });
};

// Injecter les styles CSS une seule fois
const injectPulseStyles = () => {
  if (document.getElementById('pulse-marker-styles')) return;
  
  const style = document.createElement('style');
  style.id = 'pulse-marker-styles';
  style.textContent = `
    @keyframes pulse-expand {
      0% { 
        transform: translate(-50%, -50%) scale(0.3); 
        opacity: 0.5; 
      }
      100% { 
        transform: translate(-50%, -50%) scale(1.2); 
        opacity: 0; 
      }
    }
    
    @keyframes blink {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.3; }
    }
    
    .pulsing-marker-me, .pulsing-marker-other {
      background: transparent !important;
      border: none !important;
    }
    
    .pulse-wrapper {
      overflow: visible !important;
    }
  `;
  document.head.appendChild(style);
};

// Composant pour centrer la carte
const MapController = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || map.getZoom());
    }
  }, [center, zoom, map]);
  
  return null;
};

const LocalisationTempsReelPage = () => {
  const [loading, setLoading] = useState(true);
  const [maPosition, setMaPosition] = useState(null);
  const [positionsEquipe, setPositionsEquipe] = useState([]);
  const [partageActif, setPartageActif] = useState(false);
  const [precision, setPrecision] = useState(null);
  const [derniereMaj, setDerniereMaj] = useState(null);
  const [watchId, setWatchId] = useState(null);
  const [erreurGeo, setErreurGeo] = useState(null);
  
  // Position par défaut (Barcélère - Sud-Ouest France approximatif)
  const [mapCenter, setMapCenter] = useState([43.9, 0.5]);
  const [mapZoom, setMapZoom] = useState(13);
  
  // Injecter les styles CSS pour l'animation pulsante
  useEffect(() => {
    injectPulseStyles();
  }, []);

  // Démarrer le suivi de position
  const demarrerSuivi = useCallback(() => {
    if (!navigator.geolocation) {
      setErreurGeo("Géolocalisation non supportée par votre navigateur");
      toast.error("Géolocalisation non disponible");
      return;
    }
    
    setLoading(true);
    setErreurGeo(null);
    
    // Options optimisées pour mobile
    const options = {
      enableHighAccuracy: true,  // GPS haute précision
      timeout: 30000,            // 30 secondes (plus long pour mobile)
      maximumAge: 5000           // Cache de 5 secondes max
    };
    
    // Position initiale
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          precision: position.coords.accuracy,
          altitude: position.coords.altitude,
          vitesse: position.coords.speed,
          timestamp: new Date().toISOString()
        };
        setMaPosition(pos);
        setPrecision(Math.round(position.coords.accuracy));
        setDerniereMaj(new Date());
        setMapCenter([pos.lat, pos.lng]);
        setMapZoom(17); // Zoom plus proche
        setLoading(false);
        
        toast.success("Position GPS obtenue !", {
          description: `Précision: ±${Math.round(position.coords.accuracy)}m`
        });
        
        // Envoyer au serveur si partage actif
        if (partageActif) {
          envoyerPosition(pos);
        }
      },
      (error) => {
        setErreurGeo(getGeoErrorMessage(error));
        setLoading(false);
        toast.error(getGeoErrorMessage(error));
      },
      options
    );
    
    // Suivi continu avec options adaptées mobile
    const id = navigator.geolocation.watchPosition(
      (position) => {
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          precision: position.coords.accuracy,
          altitude: position.coords.altitude,
          vitesse: position.coords.speed,
          timestamp: new Date().toISOString()
        };
        setMaPosition(pos);
        setPrecision(Math.round(position.coords.accuracy));
        setDerniereMaj(new Date());
        
        if (partageActif) {
          envoyerPosition(pos);
        }
      },
      (error) => {
        console.warn("Erreur suivi GPS:", error.message);
        // Ne pas afficher d'erreur pour les timeouts occasionnels
        if (error.code !== error.TIMEOUT) {
          toast.error(getGeoErrorMessage(error), { duration: 3000 });
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 10000  // Accepter position de 10s max pour le tracking continu
      }
    );
    
    setWatchId(id);
  }, [partageActif]);

  // Arrêter le suivi
  const arreterSuivi = useCallback(() => {
    if (watchId !== null) {
      navigator.geolocation.clearWatch(watchId);
      setWatchId(null);
    }
  }, [watchId]);

  // Envoyer la position au serveur avec plus d'infos
  const envoyerPosition = async (pos) => {
    try {
      await api.post('/localisation/partager', {
        latitude: pos.lat,
        longitude: pos.lng,
        precision: pos.precision,
        altitude: pos.altitude,
        vitesse: pos.vitesse,
        appareil: detecterAppareil(),
        batterie: await getBatteryLevel()
      });
    } catch (error) {
      console.error("Erreur envoi position:", error);
    }
  };
  
  // Récupérer le niveau de batterie (si disponible)
  const getBatteryLevel = async () => {
    try {
      if ('getBattery' in navigator) {
        const battery = await navigator.getBattery();
        return Math.round(battery.level * 100);
      }
    } catch (e) {}
    return null;
  };

  // Récupérer les positions de l'équipe
  const chargerPositionsEquipe = async () => {
    try {
      const response = await api.get('/localisation/equipe');
      setPositionsEquipe(response.data.positions || []);
    } catch (error) {
      console.error("Erreur chargement équipe:", error);
    }
  };

  // Toggle partage
  const togglePartage = async () => {
    const newState = !partageActif;
    setPartageActif(newState);
    
    if (newState) {
      toast.success("Partage de position activé", {
        description: "Votre équipe peut maintenant vous voir"
      });
      if (maPosition) {
        envoyerPosition(maPosition);
      }
    } else {
      // Masquer la position
      try {
        await api.post('/localisation/masquer');
        toast.info("Position masquée");
      } catch (e) {}
    }
  };

  // Détecter le type d'appareil
  const detecterAppareil = () => {
    const ua = navigator.userAgent.toLowerCase();
    if (/mobile|android|iphone|ipad/.test(ua)) return 'mobile';
    return 'ordinateur';
  };

  // Message d'erreur géolocalisation - amélioré pour mobile
  const getGeoErrorMessage = (error) => {
    const isMobile = /mobile|android|iphone|ipad/i.test(navigator.userAgent);
    const isHTTPS = window.location.protocol === 'https:';
    
    switch (error.code) {
      case error.PERMISSION_DENIED:
        if (isMobile) {
          return "Autorisation GPS refusée. Allez dans Paramètres > Applications > Bureau Agricole > Autorisations > Localisation.";
        }
        return "Autorisation de géolocalisation refusée. Cliquez sur l'icône cadenas dans la barre d'adresse.";
      case error.POSITION_UNAVAILABLE:
        if (isMobile) {
          return "GPS indisponible. Vérifiez que le GPS est activé dans les paramètres de votre téléphone.";
        }
        return "Position indisponible. Vérifiez votre connexion.";
      case error.TIMEOUT:
        if (isMobile) {
          return "Délai GPS dépassé. Sortez à l'extérieur pour une meilleure réception.";
        }
        return "Délai dépassé pour obtenir la position.";
      default:
        if (!isHTTPS) {
          return "La géolocalisation nécessite une connexion HTTPS sécurisée.";
        }
        return "Erreur de géolocalisation. Réessayez.";
    }
  };

  // Initialisation
  useEffect(() => {
    demarrerSuivi();
    chargerPositionsEquipe();
    
    // Rafraîchir les positions équipe toutes les 30s
    const interval = setInterval(chargerPositionsEquipe, 30000);
    
    return () => {
      arreterSuivi();
      clearInterval(interval);
    };
  }, []);

  // Centrer sur ma position
  const centrerSurMoi = () => {
    if (maPosition) {
      setMapCenter([maPosition.lat, maPosition.lng]);
      setMapZoom(16);
    }
  };

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col">
      {/* Header */}
      <div className="p-4 bg-background border-b flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-green-100">
            <Navigation className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h1 className="font-bold text-lg">Localisation Temps Réel</h1>
            <p className="text-sm text-muted-foreground">
              {maPosition ? `${maPosition.lat.toFixed(5)}, ${maPosition.lng.toFixed(5)}` : 'Recherche GPS...'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Précision */}
          {precision && (
            <Badge variant="outline" className="gap-1">
              <Zap className="w-3 h-3" />
              ±{precision}m
            </Badge>
          )}
          
          {/* Dernière MAJ */}
          {derniereMaj && (
            <Badge variant="secondary" className="gap-1">
              <Clock className="w-3 h-3" />
              {derniereMaj.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </Badge>
          )}
          
          {/* Toggle partage */}
          <div className="flex items-center gap-2">
            <Switch 
              checked={partageActif} 
              onCheckedChange={togglePartage}
              id="partage"
            />
            <Label htmlFor="partage" className="flex items-center gap-1 cursor-pointer">
              {partageActif ? (
                <>
                  <Wifi className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-600">Visible</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Masqué</span>
                </>
              )}
            </Label>
          </div>
          
          {/* Actions */}
          <Button onClick={centrerSurMoi} variant="outline" size="sm">
            <MapPin className="w-4 h-4 mr-1" />
            Centrer
          </Button>
          
          <Button onClick={demarrerSuivi} variant="outline" size="icon">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Carte */}
      <div className="flex-1 relative">
        {erreurGeo ? (
          <div className="absolute inset-0 flex items-center justify-center bg-muted/50">
            <Card className="max-w-md">
              <CardContent className="pt-6 text-center">
                <MapPin className="w-12 h-12 mx-auto text-red-500 mb-4" />
                <p className="text-lg font-medium mb-2">Erreur de géolocalisation</p>
                <p className="text-muted-foreground mb-4">{erreurGeo}</p>
                <Button onClick={demarrerSuivi}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Réessayer
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            className="h-full w-full"
            zoomControl={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            <MapController center={mapCenter} zoom={mapZoom} />
            
            {/* Ma position avec effet pulsant */}
            {maPosition && (
              <>
                {/* Cercle de précision */}
                <Circle
                  center={[maPosition.lat, maPosition.lng]}
                  radius={precision || 10}
                  pathOptions={{
                    color: '#22c55e',
                    fillColor: '#22c55e',
                    fillOpacity: 0.1,
                    weight: 1
                  }}
                />
                
                {/* Marqueur pulsant */}
                <Marker 
                  position={[maPosition.lat, maPosition.lng]}
                  icon={createPulsingIcon('#22c55e', true)}
                >
                  <Popup>
                    <div className="text-center">
                      <div className="font-bold flex items-center gap-1 justify-center">
                        <User className="w-4 h-4" />
                        Ma position
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {detecterAppareil() === 'mobile' ? (
                          <span className="flex items-center gap-1 justify-center">
                            <Smartphone className="w-3 h-3" /> Mobile
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 justify-center">
                            <Laptop className="w-3 h-3" /> Ordinateur
                          </span>
                        )}
                      </div>
                      <div className="text-xs mt-1">
                        Précision: ±{precision}m
                      </div>
                    </div>
                  </Popup>
                </Marker>
              </>
            )}
            
            {/* Positions de l'équipe */}
            {positionsEquipe.map((membre) => (
              <Marker
                key={membre.user_id}
                position={[membre.latitude, membre.longitude]}
                icon={createPulsingIcon(membre.couleur || '#3b82f6', false)}
              >
                <Popup>
                  <div className="text-center">
                    <div className="font-bold">{membre.prenom} {membre.nom}</div>
                    <div className="text-xs text-muted-foreground">
                      {membre.appareil === 'mobile' ? (
                        <span className="flex items-center gap-1 justify-center">
                          <Smartphone className="w-3 h-3" /> Mobile
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 justify-center">
                          <Laptop className="w-3 h-3" /> Ordinateur
                        </span>
                      )}
                    </div>
                    <div className="text-xs mt-1">
                      Mis à jour: {new Date(membre.timestamp).toLocaleTimeString('fr-FR')}
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}
        
        {/* Loading overlay */}
        {loading && !maPosition && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-[1000]">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-green-600" />
              <p className="mt-2 text-muted-foreground">Recherche de votre position...</p>
            </div>
          </div>
        )}
      </div>
      
      {/* Info équipe */}
      {positionsEquipe.length > 0 && (
        <div className="p-2 bg-muted/50 border-t">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Eye className="w-4 h-4" />
            <span>{positionsEquipe.length} membre(s) visible(s)</span>
            <div className="flex -space-x-2 ml-2">
              {positionsEquipe.slice(0, 5).map((m, i) => (
                <div 
                  key={m.user_id}
                  className="w-6 h-6 rounded-full bg-blue-500 border-2 border-white flex items-center justify-center text-white text-xs font-bold"
                  title={`${m.prenom} ${m.nom}`}
                >
                  {m.prenom?.[0]}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocalisationTempsReelPage;
